import React, {useEffect, useRef, useState} from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import {
    ActiveIteration,
    closeCount,
    CountResult,
    createCount,
    fetchTrucks,
    searchCounts,
    SearchUsersResult,
    SpotSample
} from '../../utils'
import {CachedProduct, getCachedProducts} from '../../productCache'
import Notifications, {Notification} from '../../../common/Notifications'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import {webEvents} from '../../../common/WebEvents'
import Alert from '@mui/material/Alert'
import CheckIcon from '@mui/icons-material/Check'
import CountsTable from './CountsTable'
import {useAuth} from '../../../auth/auth'
import {useLocation, useNavigate} from 'react-router-dom'
import CountsSummaryBox from '../CountsSummaryBox'
import {useDispatch} from 'react-redux'
import {createError} from '../../../store/slices/error'
import {H3User} from '../../../auth'
import UserAutocomplete from './UserAutocomplete'
import CloseIcon from '@mui/icons-material/Close'
import {NotificationSnackbar} from '../../../common/ui'
import StepComponent from '../../../common/StepComponent'

type Truck = {
    location_id: string,
    contact_name: string
}

type CountData = {
    countType?: string | undefined,
    spotSamples: SpotSample[],
    truck: Truck[],
    description?: string | null
}

function Manage() {
    const {getTruck, getUser, getPermissions} = useAuth()
    const truckName = getTruck()
    const [activeIter, setActiveIter] = useState<ActiveIteration | null>(null)
    const [initiateCount, setInitiateCount] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)
    const [countData, setCountData] = useState<CountData>({spotSamples: [], truck: []})
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [trucks, setTrucks] = useState<Truck[]>([])
    const [selectedSample, setSelectedSample] = useState<CachedProduct | null>(null)
    const [openDialog, setOpenDialog] = useState(false)
    const [closeCountDialog, setCloseCountDialog] = useState(false)
    const [selectedCountId, setSelectedCountId] = useState<number | null>(null)
    const [countSubmitted, setCountSubmitted] = useState<boolean | null>(null)
    const [countClosed, setCountClosed] = useState<boolean>(false)
    const [deserializedNotifications, setDeserializedNotifications] = useState<
        Notification[]
    >([])
    const [entryErrors, setEntryErrors] = useState<string | null>(null)
    const [inputValue, setInputValue] = useState('')
    const dispatch = useDispatch()

    const currentUser = getUser()

    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const [reportSubmitted, setReportSubmittedAlert] = useState(false)
    const [requestedBy, setRequestedBy] = useState<SearchUsersResult | H3User | null>(currentUser)
    const [searchPermission, setSearchPermission] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        const submissionSuccessItem = window.sessionStorage.getItem('submissionSuccess')
        if (submissionSuccessItem === 'true') {
            setReportSubmittedAlert(true)
            setActiveIter(null)
            window.sessionStorage.setItem('submissionSuccess', 'false')
            return
        }
        let isMounted = true
        let activeIter: ActiveIteration | null = null

        if (!openDialog) {
            setRequestedBy({full_name: currentUser.full_name as string, user_id: currentUser.user_id})
        }

        if (getPermissions().includes('search-users')) {
            setSearchPermission(true)
        }

        /* A truck can only have one active iteration. This means the number of counts
         * returned below can only be 0 or 1.
         */
        searchCounts({
            location_ids: truckName,
            iteration_statuses: 'pending,in-progress'
        })
            .then((counts: CountResult[]) => {
                if (!isMounted) return

                if (counts.length) {
                    let c = counts[0]
                    let iter = c.iteration

                    activeIter = {
                        countId: c.countId,
                        iterationId: iter.iterationId,
                        type: c.type,
                        status: iter.status,
                        sample: iter.sample,
                        description: c.description,
                        assignedAt: iter.assignedAt,
                        requestedBy: iter.requestedBy,
                        requestedAt: iter.requestedAt
                    }

                    setActiveIter(activeIter)
                } else {
                    setActiveIter(null)
                }
            })
            .catch(error => {
                if (isMounted) {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }
            })

        const fetchProducts = async () => {
            try {
                let cachedProducts = await getCachedProducts()
                setProducts(Object.values(cachedProducts))
            } catch (error: any) {
                if (isMounted) {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }
            }

            fetchTrucks().then(setTrucks)
        }

        fetchProducts()

        return () => {
            isMounted = false
        }
    }, [location.state, openDialog, closeCountDialog])

    function playAudio() {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    const handleSelectCountType = (
        event: any,
        value: string | null
    ) => {
        const countType = typeof value === 'string' ? value : undefined

        setCountData(() => ({
            countType: countType,
            spotSamples: [],
            truck: []
        }))
    }

    const handleSelectingSamples = (event: any, value: any) => {
        if (!value) return

        let sku = value.sku
        let model = value.model

        let exists = countData.spotSamples.some(sample =>
            sample.sku === sku && sample.model === model
        )

        if (!exists) {
            setCountData(prevState => ({
                ...prevState,
                spotSamples: [
                    ...prevState.spotSamples,
                    {sku, model}
                ]
            }))
            setSelectedSample(null)
        }
    }

    const handleSelectTruck = (event: React.ChangeEvent<{}>, value: Truck | null) => {
        if (!value) return

        const truck = trucks.find(t => t.location_id === value.location_id)
        if (!truck) return

        if (countData.countType === 'spot') {
            setCountData(prevState => ({
                ...prevState,
                truck: [{location_id: truck.location_id, contact_name: truck.contact_name}]
            }))
        } else {
            const exists = countData.truck.some(tk => tk.location_id === truck.location_id)
            if (!exists) {
                setCountData(prevState => ({
                    ...prevState,
                    truck: [
                        ...prevState.truck,
                        {location_id: truck.location_id, contact_name: truck.contact_name}
                    ]
                }))
            }
        }
    }

    const handleRemoveSpotSample = (index: number) => {
        let updatedSamples = [...countData.spotSamples]
        updatedSamples.splice(index, 1)
        setCountData({...countData, spotSamples: updatedSamples})
    }

    const handleRemoveTruck = (index: number) => {
        let updatedTrucks = [...countData.truck]
        updatedTrucks.splice(index, 1)
        setCountData({...countData, truck: updatedTrucks})
    }

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let {value} = event.target
        setCountData(prevState => ({
            ...prevState,
            description: value
        }))
    }

    const handleCancel = () => {
        setInitiateCount(false)
        setCurrentStep(1)
        setCountData({spotSamples: [], truck: []})
    }

    const renderDescription = () => {
        return (
            <div className="flex-1 rounded-lg">
                <TextField
                    className="w-full"
                    multiline
                    rows={4}
                    value={countData.description}
                    label="Description"
                    disabled={currentStep === 3}
                    onChange={handleDescriptionChange}
                />
            </div>
        )
    }

    const confirmSubmit = () => {
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
    }

    const handleConfirm = async () => {
        await submitCount()
        handleClose()
        handleCancel()
    }

    const submitCount = async () => {
        if (countData.countType === 'spot') {
            let spotSkus = countData.spotSamples?.map((sample: SpotSample) => (sample.sku))
            try {
                await createCount({
                    type: 'spot',
                    location_id: countData.truck[0].location_id,
                    sample: spotSkus,
                    description: countData.description,
                    on_behalf_of: requestedBy?.user_id
                })
                setCountSubmitted(true)
            } catch (error: any) {
                console.error('Error initiating count:', error)
                webEvents.error(
                    error.message,
                    {
                        count: {
                            type: 'spot',
                            location_id: countData.truck[0].location_id,
                            sample: spotSkus,
                            description: countData.description
                        }
                    },
                    error.stack
                )
                playAudio()
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
                setCountSubmitted(false)
            }
        } else {
            for (let truck of countData.truck) {
                try {
                    await createCount({
                        type: countData.countType || 'full',
                        location_id: truck.location_id,
                        description: countData.description,
                        on_behalf_of: requestedBy?.user_id
                    })
                    setCountSubmitted(true)
                } catch (error: any) {
                    console.error('Error initiating count:', error)
                    webEvents.error(
                        error.message,
                        {
                            count: {
                                type: countData.countType,
                                location_id: truck.location_id,
                                description: countData.description
                            }
                        },
                        error.stack
                    )
                    playAudio()
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                    setCountSubmitted(false)
                }
            }
        }
    }

    const confirmClose = (countId: number) => {
        setSelectedCountId(countId)
        setCloseCountDialog(true)
    }

    const handleCloseCountDialog = () => {
        setCloseCountDialog(false)
        setSelectedCountId(null)
    }

    const handleConfirmCloseCount = async () => {
        if (selectedCountId !== null) {
            await handleCloseCount(selectedCountId)
            handleCloseCountDialog()
        }
    }

    const handleCloseCount = async (countId: number) => {
        try {
            await closeCount(countId)
            setCountClosed(true)
        } catch (error: any) {
            console.error('Error closing count:', error)
            webEvents.error(
                error.message,
                {
                    count: {
                        id: countId
                    }
                },
                error.stack
            )
            playAudio()
            const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
            dispatch(createError({
                type: error.type,
                message: causeMessage
            }))
            setCountClosed(false)
        }
    }

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep((prevStep) => prevStep - 1)
    }

    const handleStepChange = () => {
        if (currentStep === 3) {
            confirmSubmit()
        } else {
            setCurrentStep(currentStep + 1)
        }
    }

    const getNextButtonLabel = () => {
        if (currentStep === 4) return 'Create Count'
        return 'Next'
    }

    const isStepDisabled = (type: 'back' | 'next') => {
        if (type === 'back') {
            return currentStep === 1
        }

        if (type === 'next') {
            if (currentStep === 1) {
                if (!countData.countType) return true
                if (countData.countType === 'spot' && countData.spotSamples.length === 0) return true
            }
            if (currentStep === 2) {
                if (countData.truck.length === 0) return true
            }
            if (currentStep === 3) {
                if (searchPermission && !requestedBy) return true
            }
        }
        return false
    }

    const stepComponent = (
        <Box p={3} width="100%">
            {currentStep === 1 && (
                <Box>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                            <Autocomplete
                                disablePortal
                                id="countType"
                                options={['spot', 'full', 'quarterly']}
                                getOptionLabel={(type) =>
                                    type.charAt(0).toUpperCase()
                                    + type.slice(1)}
                                value={countData.countType || null}
                                onChange={handleSelectCountType}
                                renderInput={(params) => <TextField required {...params}
                                                                    label="Select Count Type" />}
                            />

                            {countData.countType === 'spot' && (
                                <Box mt={2}>
                                    <Autocomplete
                                        disablePortal
                                        id="sampleSelection"
                                        options={products}
                                        getOptionLabel={(product) => `${product.sku}, ${product.model}`}
                                        value={selectedSample}
                                        onChange={(event, newValue) => {
                                            handleSelectingSamples(event, newValue)
                                            setInputValue('')
                                        }}
                                        inputValue={inputValue}
                                        onInputChange={(event, newInputValue) => {
                                            setInputValue(newInputValue)
                                        }}
                                        renderInput={(params) => (
                                            <TextField required {...params} label="Select sample" />
                                        )}
                                    />
                                    {countData.spotSamples.length === 0 &&
                                        <Typography color="error" variant="body2">Please select the
                                            sample of items to include in the count</Typography>}
                                </Box>
                            )}
                        </Box>
                        {countData.countType === 'spot' && renderDescription()}
                    </Box>

                    {countData.countType === 'spot' && countData.spotSamples.length > 0 && (
                        <Box overflow="auto" mt={2} mb={2}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell>Model</TableCell>
                                            <TableCell>Remove</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {countData.spotSamples.map((sample, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{sample.sku}</TableCell>
                                                <TableCell>{sample.model}</TableCell>
                                                <TableCell>
                                                    <IconButton size="small"
                                                                onClick={() => handleRemoveSpotSample(index)}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>
            )}
            {currentStep === 2 && (
                <Box>
                    <Autocomplete
                        disablePortal
                        id="truck"
                        options={trucks}
                        getOptionLabel={(truck) => truck.location_id}
                        isOptionEqualToValue={(option, value) => option.location_id === value?.location_id}
                        sx={{width: 300}}
                        value={countData.truck[0] || null}
                        onChange={handleSelectTruck}
                        renderInput={(params) => <TextField required {...params}
                                                            label="Select a Truck" />}
                    />

                    {countData.countType === 'full' && countData.truck.length > 0 && (
                        <Box overflow="auto" mt={2}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Truck</TableCell>
                                            <TableCell>Assigned To</TableCell>
                                            <TableCell>Remove</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {countData.truck.map((location, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{location.location_id}</TableCell>
                                                <TableCell>{location.contact_name}</TableCell>
                                                <TableCell>
                                                    <IconButton size="small"
                                                                onClick={() => handleRemoveTruck(index)}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>
            )}
            {currentStep === 3 && (
                <>
                    {searchPermission ? (
                        <UserAutocomplete
                            searchParams={{
                                types: 'regional-manager',
                                banned: false,
                                page_size: 1000
                            }}
                            handleSelect={setRequestedBy}
                            label={'Select Requester'}
                            requestedBy={requestedBy as SearchUsersResult}
                            searchPermission={searchPermission}
                        />
                    ) : (
                        <TextField
                            sx={{width: 300, marginTop: 1}}
                            value={requestedBy ? `${requestedBy.full_name} (${requestedBy.user_id})` : ''}
                            label={'Requester'}
                            disabled
                        />
                    )}
                </>
            )}
            {currentStep === 4 && (
                <Box>
                    {countData.countType === 'spot' && (
                        <>
                            <Box overflow="auto" mt={2}>
                                <Typography bgcolor="grey.100" px={2} py={1} textAlign="center"
                                            fontWeight="bold" variant="h4">Spot Count</Typography>
                                <Box bgcolor="grey.100" px={2} py={1} textAlign="center"
                                     fontWeight="medium">{`A spot count for ${(countData).truck[0].location_id} will be assigned to ${(countData).truck[0].contact_name}. Please confirm and submit.`}</Box>
                                <Box bgcolor="grey.100" px={2} py={1} textAlign="center"
                                     fontWeight="medium">{requestedBy ? `Requester: ${requestedBy.full_name}` : `Requester: ${currentUser.full_name}`}</Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell>Model</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {countData.spotSamples.map((sample, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{sample.sku}</TableCell>
                                                    <TableCell>{sample.model}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {renderDescription()}
                            </Box>
                        </>
                    )}
                    {countData.countType === 'full' && (
                        <>
                            <Box overflow="auto" mt={2}>
                                <Typography bgcolor="grey.100" px={2} py={1} textAlign="center"
                                            fontWeight="bold" variant="h4">Full Count</Typography>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Truck</TableCell>
                                                <TableCell>Assigned To</TableCell>
                                                <TableCell>Requester</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {countData.truck.map((tk, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{tk.location_id}</TableCell>
                                                    <TableCell>{tk.contact_name}</TableCell>
                                                    <TableCell>{requestedBy ? requestedBy.full_name : currentUser.full_name}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Box>
    )

    return (
        <>
            {entryErrors && (
                <NotificationSnackbar
                    open={true}
                    message={entryErrors}
                    onClose={() => setEntryErrors(null)}
                    severity="error"
                    autoHideDuration={3000}
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                />
            )}
            {countSubmitted && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit" />} severity="success"
                    onClose={() => setCountSubmitted(false)}>
                    Count successfully created!
                </Alert>
            )}
            {countClosed && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit" />} severity="success"
                    onClose={() => setCountClosed(false)}>
                    Count successfully closed!
                </Alert>
            )}
            {reportSubmitted && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit" />} severity="success"
                    onClose={() => setReportSubmittedAlert(false)}>
                    Report successfully submitted for review!
                </Alert>
            )}
            <Box position="fixed" top={30} right={80} zIndex={50}>
                <Notifications notificationList={deserializedNotifications} />
            </Box>
            <audio ref={audioPlayer} src={excludedSound} />
            <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center"
                 height="100vh" width="100%">
                <Box display="flex" flexDirection="column" width="100%" alignItems="center" gap={2}>
                    {!initiateCount && (
                        <>
                            <Box mt={5} display="flex" flexDirection="column" justifyContent="center" width="90%">
                                <CountsSummaryBox />
                                <Box display="flex" flexDirection="row" justifyContent="space-between"
                                     sx={{marginBottom: 3, marginTop: 3}}>
                                    <Button onClick={() => setInitiateCount(!initiateCount)} variant="contained"
                                            sx={{width: '100%', maxWidth: 200, margin: 'auto'}}>
                                        Create New Count
                                    </Button>
                                    {activeIter && truckName && (
                                        <Box display="flex" flexDirection="column" justifyContent="center"
                                             alignSelf="flex-end"
                                             sx={{
                                                 marginX: 3,
                                                 textAlign: 'center',
                                                 padding: 2,
                                                 border: '1px solid #ccc',
                                                 borderRadius: 1,
                                                 backgroundColor: 'white',
                                                 boxShadow: 1
                                             }}>
                                            <Typography variant="h6">{truckName} count
                                                status: {activeIter.status}</Typography>
                                            <Button onClick={() => navigate('/inventory/counts/conduct')}
                                                    variant="contained"
                                                    sx={{marginTop: 3, marginX: 'auto', maxWidth: 200}}>
                                                {activeIter?.status === 'pending' ? 'Begin count' : 'Resume count'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <CountsTable countClosed={countClosed} confirmClose={confirmClose} />
                            </Box>
                            <Box mt={10} display="flex" justifyContent="center" width="80%">
                            </Box>
                        </>
                    )}
                </Box>

                {initiateCount && (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '1200px'}}>
                        <StepComponent
                            sx={{
                                borderRadius: '8px',
                                boxShadow: 3,
                                marginX: 'auto',
                                marginTop: 8,
                                width: '100%',
                                maxWidth: '600px'
                            }}
                            stepComponent={stepComponent}
                            currentStep={currentStep}
                            handleBack={handleBack}
                            handleStepChange={handleStepChange}
                            confirmSubmit={confirmSubmit}
                            handleClose={handleClose}
                            handleConfirm={handleConfirm}
                            dialogContent={'Please confirm if you wish to continue with count assignment.'}
                            dialogTitle={'Confirm Submission'}
                            dialogActions={{
                                submitLabel: 'Create Count'
                            }}
                            openDialog={openDialog}
                            isStepDisabled={isStepDisabled}
                            getNextButtonLabel={getNextButtonLabel}
                        />
                    </Box>
                )}

                <Dialog
                    open={closeCountDialog}
                    onClose={handleCloseCountDialog}
                    closeAfterTransition={false}>
                    <DialogTitle>
                        Confirm Iteration Closure
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            You are about to close the iteration. Please confirm if you wish to
                            continue.
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                        <Button onClick={handleCloseCountDialog} color="error">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmCloseCount}
                            color="primary">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    )
}

export default Manage
